// popup พรีวิวไฟล์
//
// รูปกับ pdf ให้ browser เรนเดอร์เองผ่าน <img>/<iframe> — ไม่ต้องโหลดอะไรเพิ่ม
// xlsx/pptx/docx ต้องแกะเนื้อในเอง (ดู lib/office-preview.ts) จึงโหลดตัวแกะแบบ
// dynamic import เฉพาะตอนเปิดไฟล์ชนิดนั้น ไม่ให้ไปถ่วง bundle ของหน้าอื่น
import { useEffect, useRef, useState } from "react";
import { IconClose, IconDownload } from "@shared/ui/icons";
import { documentFileUrl, type CampaignDocument } from "../api/documents-api";
import { fileKind, fmtBytes, kindLabel, type FileKind } from "../lib/file-kind";
import type { SheetPreview, SlideData } from "../lib/office-preview";

type Parsed =
  | { kind: "sheet"; data: SheetPreview }
  | { kind: "slides"; data: SlideData[] }
  | { kind: "doc"; data: string[] }
  | { kind: "text"; data: string };

export function PreviewModal({ doc, onClose }: { doc: CampaignDocument; onClose: () => void }) {
  const kind = fileKind(doc);
  const url = documentFileUrl(doc.id);
  const closeRef = useRef<HTMLButtonElement>(null);

  // Esc ปิด และล็อกการเลื่อนของหน้าหลังไว้ ไม่ให้เลื่อนตามล้อเมาส์ขณะเปิด popup
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    // คลิกฉากหลังเพื่อปิด แต่คลิกในกล่องต้องไม่ปิด (stopPropagation ที่กล่อง)
    <div className="doc-modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="doc-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={doc.name}
      >
        <div className="doc-modal-head">
          <div className="doc-modal-title">
            <strong title={doc.name}>{doc.name}</strong>
            <span className="muted">
              {kindLabel(kind)} · {fmtBytes(doc.size)}
            </span>
          </div>
          <div className="doc-modal-actions">
            <a
              className="btn-outline inline-btn"
              href={documentFileUrl(doc.id, { download: true })}
              download={doc.name}
            >
              <IconDownload size={16} /> ดาวน์โหลด
            </a>
            <button ref={closeRef} type="button" className="icon-btn" onClick={onClose}>
              <IconClose size={18} />
            </button>
          </div>
        </div>

        <div className="doc-modal-body">
          {kind === "image" ? (
            <img className="doc-modal-img" src={url} alt={doc.name} />
          ) : kind === "pdf" ? (
            <iframe className="doc-modal-frame" src={url} title={doc.name} />
          ) : kind === "other" ? (
            <UnsupportedView doc={doc} />
          ) : (
            <ParsedView doc={doc} kind={kind} />
          )}
        </div>
      </div>
    </div>
  );
}

function UnsupportedView({ doc }: { doc: CampaignDocument }) {
  return (
    <div className="panel empty-state">
      <h3>ไฟล์ชนิดนี้เปิดดูในหน้าเว็บไม่ได้</h3>
      <p className="muted">
        {doc.mime || "ไม่ทราบชนิดไฟล์"} · {fmtBytes(doc.size)} —
        กดดาวน์โหลดเพื่อเปิดด้วยโปรแกรมในเครื่อง
      </p>
    </div>
  );
}

/** ชนิดที่ต้องแกะเนื้อในเอง — โหลดไฟล์ + ตัวแกะตอนเปิดเท่านั้น */
function ParsedView({ doc, kind }: { doc: CampaignDocument; kind: FileKind }) {
  const [state, setState] = useState<
    { s: "loading" } | { s: "error"; msg: string } | { s: "ok"; parsed: Parsed }
  >({ s: "loading" });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(documentFileUrl(doc.id));
        if (!res.ok) throw new Error(`โหลดไฟล์ไม่สำเร็จ (HTTP ${res.status})`);

        let parsed: Parsed;
        if (kind === "text") {
          parsed = { kind: "text", data: await res.text() };
        } else {
          const buf = await res.arrayBuffer();
          const office = await import("../lib/office-preview");
          if (kind === "sheet")
            parsed = { kind: "sheet", data: await office.readSpreadsheet(buf, doc.name) };
          else if (kind === "slides")
            parsed = { kind: "slides", data: await office.readSlides(buf) };
          else parsed = { kind: "doc", data: await office.readDocx(buf) };
        }
        if (alive) setState({ s: "ok", parsed });
      } catch (e) {
        if (alive)
          setState({ s: "error", msg: e instanceof Error ? e.message : "อ่านไฟล์ไม่สำเร็จ" });
      }
    })();
    return () => {
      alive = false;
    };
  }, [doc.id, doc.name, kind]);

  if (state.s === "loading") return <p className="muted doc-modal-note">กำลังอ่านไฟล์…</p>;
  if (state.s === "error")
    return (
      <div className="panel empty-state">
        <h3>พรีวิวไฟล์นี้ไม่ได้</h3>
        <p className="muted">{state.msg} — ดาวน์โหลดไปเปิดด้วยโปรแกรมในเครื่องได้ตามปกติ</p>
      </div>
    );

  const p = state.parsed;
  if (p.kind === "sheet") return <SheetView data={p.data} />;
  if (p.kind === "slides") return <SlidesView slides={p.data} />;
  if (p.kind === "doc")
    return (
      <div className="doc-text">
        {p.data.map((line, i) => (
          <p key={i}>{line}</p>
        ))}
      </div>
    );
  return <pre className="doc-pre">{p.data}</pre>;
}

function SheetView({ data }: { data: SheetPreview }) {
  const [at, setAt] = useState(0);
  const sheet = data.sheets[at] ?? data.sheets[0];
  const [head, ...body] = sheet.rows;
  const width = Math.max(head?.length ?? 0, ...body.map((r) => r.length));

  return (
    <>
      {data.sheets.length > 1 && (
        <div className="doc-tabs">
          {data.sheets.map((s, i) => (
            <button
              key={s.name + i}
              type="button"
              className={i === at ? "doc-tab on" : "doc-tab"}
              onClick={() => setAt(i)}
            >
              {s.name}
            </button>
          ))}
        </div>
      )}
      <div className="doc-sheet-wrap">
        <table className="doc-sheet">
          {head && (
            <thead>
              <tr>
                {Array.from({ length: width }, (_, i) => (
                  <th key={i}>{head[i] ?? ""}</th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {body.map((row, r) => (
              <tr key={r}>
                {Array.from({ length: width }, (_, i) => (
                  <td key={i}>{row[i] ?? ""}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="muted doc-modal-note">
        แสดงค่าในเซลล์เท่านั้น — สูตร รูปแบบตัวเลข สี และกราฟไม่ถูกนำมาแสดง
        {data.truncated && " · ไฟล์ใหญ่ ตัดแสดงบางส่วน"}
      </p>
    </>
  );
}

function SlidesView({ slides }: { slides: SlideData[] }) {
  return (
    <>
      <div className="doc-slides">
        {slides.map((s, i) => (
          <div className="doc-slide" key={i}>
            <span className="doc-slide-no">{i + 1}</span>
            <div>
              <strong>{s.title}</strong>
              {s.lines.map((l, j) => (
                <p key={j}>{l}</p>
              ))}
            </div>
          </div>
        ))}
      </div>
      <p className="muted doc-modal-note">
        เป็นข้อความในสไลด์ ไม่ใช่ภาพสไลด์จริง — การเรนเดอร์สไลด์ต้องส่งไฟล์ออกไปให้บริการภายนอก
        ระบบนี้จึงไม่ทำ ถ้าต้องดูของจริงให้ดาวน์โหลดไปเปิด
      </p>
    </>
  );
}
